/**
 * Questionnaire form behaviour: the conditional "First name" field and the
 * inline validation messages.
 *
 * Validation is deliberately left to the browser rather than done on submit.
 * Shopify's spam protection listens for `submit` on the document, fetches an
 * hCaptcha token, then re-submits the form programmatically — and a scripted
 * submit fires no `submit` event, so anything checked in a submit handler is
 * skipped entirely. Native constraint validation runs *before* the event is
 * dispatched at all, so an invalid form never reaches that listener.
 *
 * What is left to do here is presentation: swap the browser's default bubble
 * for the inline messages in the design, and express "tick at least one", which
 * HTML cannot describe on its own.
 */
if (!customElements.get('questionnaire-form')) {
  const QuestionnaireForm = class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.conditional = this.querySelector('[data-questionnaire-conditional]');
      this.shareInputs = this.querySelectorAll('[data-questionnaire-share]');
      this.anyGroup = this.querySelector('[data-questionnaire-require-any]');
      this.submitButton = this.querySelector('[type="submit"]');
      this.pendingScroll = null;

      // `invalid` does not bubble, so it has to be caught on the way down.
      this.form.addEventListener('invalid', this.handleInvalid.bind(this), true);
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      this.form.addEventListener('change', this.handleInteraction.bind(this));
      this.form.addEventListener('input', this.handleInteraction.bind(this));

      this.syncConditional();
      this.syncAnyGroup();
    }

    /**
     * Fired by the browser for each invalid control before it blocks the
     * submit. Cancelling it suppresses the default bubble, leaving the inline
     * message as the only thing shown.
     * @param {Event} evt
     */
    handleInvalid(evt) {
      evt.preventDefault();

      const group = evt.target.closest('.questionnaire__group');
      if (!group) return;

      group.dataset.questionnaireTouched = 'true';
      QuestionnaireForm.setGroupError(group, true);

      // Every invalid control fires its own event; scroll once, to the first.
      if (this.pendingScroll) return;
      this.pendingScroll = group;
      requestAnimationFrame(() => {
        this.pendingScroll.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.pendingScroll = null;
      });
    }

    /**
     * Only reached when the form is valid, since the browser blocks it
     * otherwise. Kept as a backstop for a programmatic submit: stopping
     * propagation here also keeps Shopify's captcha listener from starting the
     * token exchange that would re-submit the form behind our back.
     * @param {SubmitEvent} evt
     */
    handleSubmit(evt) {
      if (!this.form.checkValidity()) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        return;
      }

      // Submitting for real now. Lock the button so an impatient second click
      // cannot send a duplicate, but leave it enabled long enough for the
      // browser to include it in the submission.
      if (this.submitButton) {
        setTimeout(() => {
          this.submitButton.disabled = true;
        }, 0);
      }
    }

    /**
     * Once a group has been through a failed submit it re-checks itself on
     * every edit, so the red state follows the field: filled in clears it,
     * emptying it again brings it back.
     * @param {Event} evt
     */
    handleInteraction(evt) {
      if (evt.target.matches('[data-questionnaire-share]')) this.syncConditional();
      if (this.anyGroup && this.anyGroup.contains(evt.target)) this.syncAnyGroup();

      const group = evt.target.closest('.questionnaire__group');
      if (group && group.dataset.questionnaireTouched) {
        QuestionnaireForm.setGroupError(group, !QuestionnaireForm.isGroupValid(group));
      }

    }

    /**
     * Shows or hides the first-name field to match the sharing answer, keeping
     * `required` in step so a hidden field can never block the form.
     */
    syncConditional() {
      if (!this.conditional) return;

      const shared = [...this.shareInputs].some(
        (input) => input.checked && input.dataset.questionnaireShare === 'yes'
      );

      this.conditional.hidden = !shared;
      this.conditional.querySelectorAll('input').forEach((input) => {
        input.required = shared;
        input.setAttribute('aria-required', shared ? 'true' : 'false');
      });

      // Hiding it resets its state, so revealing it again never starts out red.
      if (!shared) {
        delete this.conditional.dataset.questionnaireTouched;
        QuestionnaireForm.setGroupError(this.conditional, false);
      }
    }

    /**
     * HTML has no "at least one of these boxes" rule — `required` on a checkbox
     * demands that exact box. Marking a single box required while none is
     * ticked gets the browser to enforce the group, and clearing it the moment
     * anything is ticked keeps the rest optional.
     */
    syncAnyGroup() {
      if (!this.anyGroup) return;

      const boxes = [...this.anyGroup.querySelectorAll('input[type="checkbox"]')];
      if (!boxes.length) return;

      const chosen = boxes.some((box) => box.checked);
      boxes.forEach((box) => {
        box.required = false;
      });
      if (!chosen) boxes[0].required = true;
    }

    /**
     * @param {HTMLElement} group
     * @returns {HTMLElement[]} the inputs that carry the group's validity.
     */
    static controlsOf(group) {
      const selector =
        'questionnaireRequireAny' in group.dataset ? 'input[type="checkbox"]' : '[required]';
      return [...group.querySelectorAll(selector)];
    }

    /**
     * @param {HTMLElement} group
     * @returns {boolean}
     */
    static isGroupValid(group) {
      const controls = QuestionnaireForm.controlsOf(group);
      if ('questionnaireRequireAny' in group.dataset) {
        return controls.some((input) => input.checked);
      }
      return controls.every((input) => input.checkValidity());
    }

    /**
     * @param {HTMLElement} group
     * @param {boolean} show
     */
    static setGroupError(group, show) {
      group.classList.toggle('questionnaire__group--invalid', show);

      const message = group.querySelector('[data-questionnaire-error]');
      if (message) message.hidden = !show;

      QuestionnaireForm.controlsOf(group).forEach((input) => {
        input.setAttribute('aria-invalid', show ? 'true' : 'false');
      });
    }
  };

  customElements.define('questionnaire-form', QuestionnaireForm);
}
